import React from 'react';
import { toast } from 'react-hot-toast';
import { authorizeYouTube, hasYouTubeAuthorization } from '../../services/youtubeAuth';
import { fetchMyPlaylists } from '../../services/youtubePlaylists';
import { saveImportedPlaylists } from '../../services/importedPlaylists';
import { disconnectYouTube } from '../../services/libraryDeletion';
import { refreshDueProviderMirrors } from '../../services/providerRefresh';
import { syncImportedPlaylist } from '../../services/playlistSync';
import useUserStore from '../../stores/userStore';
import { describeError } from '../../utils/errorLog';
import {
  classifyPlaylistSubmission,
  emptyPlaylistSelection,
  isPlaylistSelectionDisabled,
  playlistSelectionCountLabel,
  togglePlaylistSelection,
} from '../../utils/playlistSelection';

const EMPTY_CONNECTED = [];

function playlistIdSet(playlists) {
  return new Set((playlists || []).map((playlist) => playlist.playlistId).filter(Boolean));
}

/**
 * YouTube playlist selection.
 *
 * Authorization, the temporary picker draft, and My Music stay separate.
 * A successful connect or reconnect clears the draft to zero. It does not
 * delete imported playlists. Confirm syncs the draft as it stands: an
 * already imported playlist is refreshed, and a new one is imported.
 */
export default function PlaylistPicker({
  onImported,
  onDisconnected,
  onSkip,
  skipLabel = 'Skip for now',
  connectedPlaylists = EMPTY_CONNECTED,
  onListVisibilityChange,
}) {
  const uid = useUserStore((state) => state.user?.uid ?? null);
  const connectedKey = (connectedPlaylists || [])
    .map((playlist) => playlist.playlistId)
    .filter(Boolean)
    .sort()
    .join(',');
  const connectedIds = React.useMemo(
    () => new Set(connectedKey ? connectedKey.split(',') : []),
    [connectedKey],
  );
  const tokenPresent = hasYouTubeAuthorization();
  const [authorized, setAuthorized] = React.useState(tokenPresent);
  const [connecting, setConnecting] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [playlists, setPlaylists] = React.useState([]);
  const [selected, setSelected] = React.useState(emptyPlaylistSelection);
  const [error, setError] = React.useState(null);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = React.useState(false);
  const requestRef = React.useRef(0);

  const signedIn = authorized && tokenPresent;
  const showingList = Boolean(
    signedIn && !loading && (playlists.length > 0 || connectedPlaylists.length > 0),
  );

  React.useEffect(() => {
    onListVisibilityChange?.(showingList);
  }, [showingList, onListVisibilityChange]);

  const resetDraft = React.useCallback(() => {
    setSelected(emptyPlaylistSelection());
  }, []);

  const loadPlaylists = React.useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const { playlists: items } = await fetchMyPlaylists();
      if (requestId !== requestRef.current) return;
      setPlaylists(items);
      if (items.length === 0) {
        setError('No playlists found on this YouTube account.');
      }
    } catch (loadError) {
      if (requestId !== requestRef.current) return;
      console.error('Failed to load YouTube playlists:', describeError(loadError));
      setError('Could not load your playlists. Please try again.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (signedIn && !connecting && playlists.length === 0 && !loading && !error) {
      loadPlaylists();
    }
  }, [signedIn, connecting, playlists.length, loading, error, loadPlaylists]);

  const handleConnect = async () => {
    if (connecting) return;
    // Drop the draft before the popup returns. A token that becomes valid
    // mid-request must not paint the previous checkbox state.
    requestRef.current += 1;
    resetDraft();
    setPlaylists([]);
    setError(null);
    setLoading(false);
    setConnecting(true);
    try {
      const result = await authorizeYouTube();
      if (result.ok) {
        resetDraft();
        setPlaylists([]);
        setError(null);
        setLoading(false);
        setAuthorized(true);
        if (uid) {
          Promise.resolve(refreshDueProviderMirrors(uid, {
            playlists: connectedPlaylists,
          })).catch((refreshError) => {
            console.error('Provider refresh deferred:', describeError(refreshError));
          });
        }
        return;
      }

      if (result.reason === 'cancelled') return;
      if (result.reason === 'google-required') {
        toast.error('Sign in with Google before connecting YouTube');
        return;
      }
      toast.error(result.error?.message || 'Could not connect YouTube');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirmDisconnect) {
      setConfirmDisconnect(true);
      return;
    }
    if (!uid || disconnecting) return;
    setDisconnecting(true);
    try {
      await disconnectYouTube(uid);
      requestRef.current += 1;
      setAuthorized(false);
      resetDraft();
      setPlaylists([]);
      setConfirmDisconnect(false);
      onDisconnected?.();
      toast.success('YouTube disconnected. Your Rhythmé account is still signed in.');
    } catch (disconnectError) {
      console.error('Failed to disconnect YouTube:', describeError(disconnectError));
      toast.error('Could not disconnect YouTube. Try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  const toggle = (playlistId) => {
    if (saving) return;
    setSelected((current) => togglePlaylistSelection(current, playlistId).selection);
  };

  const youtubeIds = playlistIdSet(playlists);
  const connectedOnly = connectedPlaylists.filter(
    (playlist) => playlist?.playlistId && !youtubeIds.has(playlist.playlistId),
  );
  const visiblePlaylists = [...connectedOnly, ...playlists];

  const handleImport = async () => {
    if (saving) return;
    const { targets, imports } = classifyPlaylistSubmission(
      visiblePlaylists,
      selected,
      connectedIds,
    );

    if (targets.length === 0) {
      toast.error('Select at least one playlist');
      return;
    }

    setSaving(true);
    try {
      if (imports.length > 0) {
        await saveImportedPlaylists(uid, imports);
      }
      let persisted = true;
      if (uid) {
        try {
          for (let index = 0; index < targets.length; index += 1) {
            const playlist = targets[index];
            // Sequential: each playlist is an independent sync; a later
            // failure must not hide that earlier writes already happened.
            // eslint-disable-next-line no-await-in-loop
            await syncImportedPlaylist(uid, playlist);
          }
        } catch (persistError) {
          persisted = false;
          console.error(
            'Failed to persist imported playlist library:',
            describeError(persistError),
          );
          toast.error('Could not save playlist library data');
        }
      }
      if (persisted) {
        toast.success(`Synced ${targets.length} playlist${targets.length === 1 ? '' : 's'}`);
      }
      onImported?.(targets);
    } catch (saveError) {
      console.error('Failed to save imported playlists:', describeError(saveError));
      toast.error('Could not save your playlist selection');
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (playlist) => {
    const inLibrary = connectedIds.has(playlist.playlistId);
    const checked = selected.has(playlist.playlistId);
    const disabled = saving || isPlaylistSelectionDisabled(selected, playlist.playlistId);
    const detail = inLibrary
      ? `In My Music${playlist.itemCount ? ` · ${playlist.itemCount} tracks` : ''}`
      : `${playlist.itemCount} tracks`;
    return (
      <li key={playlist.playlistId}>
        <label className={`flex items-center gap-3 rounded-lg bg-white/5 p-2 ${disabled ? '' : 'cursor-pointer hover:bg-white/10'}`}>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => toggle(playlist.playlistId)}
            disabled={disabled}
            className="h-4 w-4 shrink-0 accent-blue-500"
          />
          {playlist.thumbnail ? (
            <img
              src={playlist.thumbnail}
              alt=""
              className="h-10 w-10 rounded object-cover shrink-0"
            />
          ) : (
            <div className="h-10 w-10 rounded bg-white/10 shrink-0" />
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-white">{playlist.title}</span>
            <span className="block text-xs text-gray-400">{detail}</span>
          </span>
        </label>
      </li>
    );
  };

  if (!signedIn || connecting) {
    return (
      <div className="playlist-picker">
        <p className="text-sm text-gray-300">
          Connect YouTube to pick from your own playlists. Rhythmé asks for read-only access and
          never stores your Google credentials.
        </p>
        <button
          type="button"
          onClick={handleConnect}
          disabled={connecting}
          className="w-full py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-60"
        >
          {connecting ? 'Opening Google…' : 'Connect YouTube'}
        </button>
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="w-full py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
          >
            {skipLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="playlist-picker">
      <p className="text-sm text-white">Select playlists to add to My Music</p>
      <p className="text-xs text-gray-400">Choose up to 10 playlists.</p>
      <p className="text-xs text-gray-300" data-testid="playlist-selection-count">
        {playlistSelectionCountLabel(selected.size)}
      </p>
      {loading && <p className="text-sm text-gray-400">Loading your playlists…</p>}
      {error && <p className="text-sm text-amber-400">{error}</p>}

      {!loading && visiblePlaylists.length > 0 && (
        <ul className="playlist-picker__list space-y-2 pr-1" data-testid="playlist-picker-list">
          {visiblePlaylists.map((playlist) => renderRow(playlist))}
        </ul>
      )}

      <div className="playlist-picker__actions flex gap-2">
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            disabled={saving}
            className="flex-1 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20 transition-colors disabled:opacity-60"
          >
            {skipLabel}
          </button>
        )}
        <button
          type="button"
          onClick={handleImport}
          disabled={saving || disconnecting || loading || selected.size === 0}
          className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {saving ? 'Syncing…' : 'Sync selected'}
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {confirmDisconnect && (
          <p className="text-xs text-amber-300">
            This removes your YouTube library from Rhythmé. Your Rhythmé account stays signed in.
          </p>
        )}
        <button
          type="button"
          data-testid="disconnect-youtube"
          onClick={handleDisconnect}
          disabled={disconnecting || saving}
          className="w-full py-2 rounded-lg bg-white/5 text-xs text-gray-300 hover:bg-white/10 transition-colors disabled:opacity-60"
        >
          {disconnecting
            ? 'Disconnecting…'
            : confirmDisconnect
              ? 'Confirm disconnect YouTube'
              : 'Disconnect YouTube'}
        </button>
        {confirmDisconnect && (
          <button
            type="button"
            onClick={() => setConfirmDisconnect(false)}
            className="w-full py-1 text-xs text-gray-400 hover:text-white"
          >
            Cancel disconnect
          </button>
        )}
      </div>
    </div>
  );
}
